import React, { useContext, useEffect, useState } from 'react';
import './About.css';
import {useNavigate} from "react-router-dom";
import axiosInstance from '../../configurations/axiosInstance/AxiosInstance';
import SectionDescription from '../../composants/sectionDescription/description';
import { LoadingContext } from '../../composants/loadingContext';
import Loader from '../../composants/loader/Loader';
import LexicalViewer from '../editor/LexicalViewer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faUsers, faHeart, faRocket } from '@fortawesome/free-solid-svg-icons';

const About = () => {
  const {loading, setLoading} = useContext(LoadingContext);
  const nav = useNavigate();
  const [aPropos, setAPropos] = useState(null);

  const getAProposById = () => {
    setLoading(true);
    axiosInstance().get(
        "public/api/apropos/list"
    ).then((result)=> {
        if(result.data != null){
            setAPropos(result.data)
        }
        setLoading(false);
    }).catch((result)=> {
        setLoading(false);
    });
  }

  useEffect(
    ()=>{
        getAProposById();
        document.title='À Propos - YouthFP';
    },[]
  );

  return (
    <>
      {loading && <Loader />}
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-purple-600 via-pink-500 to-blue-500 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl mb-6 shadow-2xl">
              <FontAwesomeIcon icon={faInfoCircle} className="text-4xl" />
            </div>
            <h1 className="text-5xl md:text-6xl font-extrabold mb-4 tracking-tight">
              À Propos de Nous
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
              Notre mission, notre vision, nos valeurs
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Values Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white rounded-2xl shadow-lg p-6 border-t-4 border-purple-500 transform hover:scale-105 transition-all duration-300">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                <FontAwesomeIcon icon={faUsers} className="text-3xl text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Notre Mission</h3>
              <p className="text-gray-600">
                Promouvoir la santé sexuelle et reproductive des jeunes au Cameroun
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 border-t-4 border-pink-500 transform hover:scale-105 transition-all duration-300">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-pink-100 rounded-full mb-4">
                <FontAwesomeIcon icon={faHeart} className="text-3xl text-pink-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Nos Valeurs</h3>
              <p className="text-gray-600">
                Engagement, respect, inclusion et excellence dans tout ce que nous faisons
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 border-t-4 border-blue-500 transform hover:scale-105 transition-all duration-300">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <FontAwesomeIcon icon={faRocket} className="text-3xl text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Notre Vision</h3>
              <p className="text-gray-600">
                Une jeunesse épanouie, informée et actrice de son bien-être
              </p>
            </div>
          </div>

          {/* Content Section */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
            {aPropos?.contenu ? (
              <div className="p-8 md:p-12">
                <div className="mb-8">
                  <h2 className="text-3xl font-bold text-gray-800 mb-4 flex items-center gap-3">
                    <div className="w-1 h-8 bg-gradient-to-b from-purple-500 to-pink-500 rounded-full"></div>
                    Qui sommes-nous ?
                  </h2>
                </div>
                <div className="prose prose-lg max-w-none">
                  <LexicalViewer 
                    content={aPropos?.contenu}
                    showCard={false}
                  />
                </div>
              </div>
            ) : !loading && (
              <div className="text-center py-20 px-4">
                <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full mb-6">
                  <FontAwesomeIcon icon={faInfoCircle} className="text-5xl text-purple-500" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3">
                  Contenu en préparation
                </h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  Nous mettons à jour notre page À Propos. Revenez bientôt pour en savoir plus sur notre organisation.
                </p>
              </div>
            )}
          </div>

          {/* CTA Section */}
          <div className="mt-12 bg-gradient-to-br from-purple-600 via-pink-500 to-blue-500 rounded-2xl shadow-2xl overflow-hidden">
            <div className="px-8 py-12 text-center text-white">
              <h3 className="text-3xl font-bold mb-4">
                Rejoignez-nous dans notre mission
              </h3>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                Ensemble, construisons un avenir meilleur pour la jeunesse camerounaise
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <button
                  onClick={() => nav('/register')}
                  className="px-8 py-4 bg-white text-purple-600 rounded-xl font-bold hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
                >
                  Devenir Membre
                </button>
                <button
                  onClick={() => nav('/')}
                  className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white rounded-xl font-bold hover:bg-white/20 transition-all duration-300"
                >
                  En Savoir Plus
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default About;